import React from 'react';
import { NtilesChart, number } from 'yankees-react-utils';
import { useTranslation } from 'react-i18next';
import StatTooltip from '../../../../components/StatTooltip';

const buildSeriesData = (stats, player, percentiles) => [{
    data: stats.map((s) => {
        if (s.key) {
            let y = null;
            if (player[`${s.key}_ntile`]) y = s.reversed ? 100 - player[`${s.key}_ntile`] : player[`${s.key}_ntile`];
            return ({
                ...s,
                value: s.formatter(player[s.key]),
                y,
                ntileData: {
                    percentile: number(s.reversed ? 100 - player[`${s.key}_ntile`] : player[`${s.key}_ntile`], 0),
                    statname: s.name,
                    p25: s.formatter(percentiles[`${s.key}_p${s.reversed ? '75' : '25'}`]),
                    p50: s.formatter(percentiles[`${s.key}_p50`]),
                    p75: s.formatter(percentiles[`${s.key}_p${s.reversed ? '25' : '75'}`]),
                },
            });
        }
        return null;
    }).filter((x) => x),
}];

const Charts = ({ className, overview: { player: [player], percentiles: [percentiles] } }) => {
    const { t: translate } = useTranslation();
    const overallChart = [{
        key: 'hitfxera',
        formatter: (v) => (v ? number(v, 2) : '--'),
        name: translate('sections.reports.pitcher.hitfx-era'),
        units: '',
        lowText: 'Poor',
        highText: 'Great',
        reversed: true,
        precision: 2,
        statistic: 'HitFX2ERA',
    }, {
        key: 'ubbrate',
        formatter: (v) => (v ? number(v, 0) : '--'),
        name: translate('sections.reports.pitcher.walk-percentage'),
        units: '%',
        lowText: 'Poor',
        highText: 'Great',
        reversed: true,
        precision: 1,
        statistic: 'UBBPercent',
    }, {
        key: 'krate',
        formatter: (v) => (v ? number(v, 0) : '--'),
        name: translate('sections.reports.pitcher.strikeout-percentage'),
        units: '%',
        lowText: 'Poor',
        highText: 'Great',
        precision: 1,
        statistic: 'KPercent',
    }, {
        key: 'cq',
        formatter: (v) => (v ? number(v, 1) : '--'),
        name: translate('sections.reports.pitcher.contact-quality'),
        units: '',
        lowText: 'Poor',
        highText: 'Great',
        reversed: true,
        precision: 1,
        statistic: 'ContactQuality',
    }, {
        key: 'pitchfxera',
        formatter: (v) => (v ? number(v, 2) : '--'),
        name: translate('sections.reports.pitcher.pitchfx-era'),
        units: '',
        lowText: 'Poor',
        highText: 'Great',
        reversed: true,
        precision: 2,
        statistic: 'PitchFXERA',
    }, {
        key: 'stuffera',
        formatter: (v) => (v ? number(v, 2) : '--'),
        name: translate('sections.reports.pitcher.stuff-era'),
        units: '',
        lowText: 'Poor',
        highText: 'Great',
        reversed: true,
        precision: 2,
        statistic: 'StuffERA',
    }, {
        key: 'breakera',
        formatter: (v) => (v ? number(v, 2) : '--'),
        name: translate('sections.reports.pitcher.break-era'),
        units: '',
        lowText: 'Poor',
        highText: 'Great',
        reversed: true,
        precision: 2,
        statistic: 'BreakERA',
    }, {
        key: 'locera',
        formatter: (v) => (v ? number(v, 2) : '--'),
        name: translate('sections.reports.pitcher.location-era'),
        units: '',
        lowText: 'Poor',
        highText: 'Great',
        reversed: true,
        precision: 2,
        statistic: 'LocERA',
    }];

    const controllingTheZone = [overallChart[1], {
        key: 'stk',
        formatter: (v) => (v ? number(v, 0) : '--'),
        name: translate('sections.reports.pitcher.strike-pct'),
        units: '%',
        lowText: 'Poor',
        highText: 'Great',
        precision: 1,
        statistic: 'Stk',
    }, {
        key: 'kzon',
        formatter: (v) => (v ? number(v, 0) : '--'),
        name: translate('sections.reports.pitcher.kzon-pct'),
        units: '%',
        lowText: 'Low',
        highText: 'High',
        precision: 1,
        statistic: 'KZon',
    }, {
        key: 'firstpstk',
        formatter: (v) => (v ? number(v, 0) : '--'),
        name: translate('sections.reports.pitcher.first-pitch-strike-pct'),
        units: '%',
        lowText: 'Poor',
        highText: 'Great',
        precision: 1,
        statistic: '1PitStk',
    }, {
        key: 'wayahead',
        formatter: (v) => (v ? number(v, 0) : '--'),
        name: translate('sections.reports.pitcher.way-ahead'),
        units: '%',
        lowText: 'Poor',
        highText: 'Great',
        precision: 1,
        statistic: 'WayAhead',
    }, {
        key: 'closepitch',
        formatter: (v) => (v ? number(v, 0) : '--'),
        name: translate('sections.reports.pitcher.close-pitch-pct'),
        units: '%',
        lowText: 'Poor',
        highText: 'Great',
        precision: 1,
        statistic: 'ClosePitch',
    }, {
        key: 'wastepitch',
        formatter: (v) => (v ? number(v, 0) : '--'),
        name: translate('sections.reports.pitcher.waste-pitch-pct'),
        units: '%',
        lowText: 'Poor',
        highText: 'Great',
        reversed: true,
        precision: 1,
        statistic: 'WastedPitPercent',
    }];

    const missingBats = [overallChart[2], {
        key: 'ms',
        formatter: (v) => (v ? number(v, 0) : '--'),
        name: translate('sections.reports.pitcher.miss-pct'),
        units: '%',
        lowText: 'Poor',
        highText: 'Great',
        precision: 1,
        statistic: 'Ms',
    }, {
        key: 'msinz',
        formatter: (v) => (v ? number(v, 0) : '--'),
        name: translate('sections.reports.pitcher.miss-in-zone-pct'),
        units: '%',
        lowText: 'Poor',
        highText: 'Great',
        precision: 1,
        statistic: 'MsInZon',
    }, {
        key: 'tkstk',
        formatter: (v) => (v ? number(v, 0) : '--'),
        name: translate('sections.reports.pitcher.called-stk-pct'),
        units: '%',
        lowText: 'Poor',
        highText: 'Great',
        precision: 1,
        statistic: 'CalledStk',
    }, {
        key: 'ch',
        formatter: (v) => (v ? number(v, 0) : '--'),
        name: translate('sections.reports.pitcher.chase-pct'),
        units: '%',
        lowText: 'Poor',
        highText: 'Great',
        precision: 1,
        statistic: 'Ch',
    }, {
        key: 'putaway',
        formatter: (v) => (v ? number(v, 0) : '--'),
        name: translate('sections.reports.pitcher.put-away-pct'),
        units: '%',
        lowText: 'Poor',
        highText: 'Great',
        precision: 1,
        statistic: 'PutAwayPct',
    }];

    const limitingDamage = [overallChart[3], {
        key: 'cr',
        formatter: (v) => (v ? number(v, 1) : '--'),
        name: translate('sections.reports.pitcher.contact-results'),
        units: '',
        lowText: 'Poor',
        highText: 'Great',
        reversed: true,
        precision: 1,
        statistic: 'ContactResults',
    }, {
        key: 'gb',
        formatter: (v) => (v ? number(v, 0) : '--'),
        name: translate('baseball.abbr-groundball-pct'),
        units: '%',
        lowText: 'Poor',
        highText: 'Great',
        precision: 1,
        statistic: 'GBPercent',
    }, {
        key: 'pop',
        formatter: (v) => (v ? number(v, 0) : '--'),
        name: translate('sections.reports.pitcher.popup-pct'),
        units: '%',
        lowText: 'Poor',
        highText: 'Great',
        precision: 1,
        statistic: 'PopPercent',
    }, {
        key: 'hitvelocity',
        formatter: (v) => (v ? number(v, 0) : '--'),
        name: translate('sections.reports.pitcher.exit-velo'),
        units: 'mph',
        lowText: 'Poor',
        highText: 'Great',
        reversed: true,
        precision: 1,
        statistic: 'ExitVelo',
    }, {
        key: 'wellhit',
        formatter: (v) => (v ? number(v, 0) : '--'),
        name: translate('sections.reports.pitcher.well-hit-pct'),
        units: '%',
        lowText: 'Poor',
        highText: 'Great',
        reversed: true,
        precision: 1,
        statistic: 'WellHitPercent',
    }, {
        key: 'slg',
        formatter: (v) => (v ? number(v, true) : '--'),
        name: translate('baseball.abbr-slugging'),
        units: '',
        lowText: 'Poor',
        highText: 'Great',
        reversed: true,
        precision: 3,
        statistic: 'SLG',
    }];

    return (
        <div className={`${className} text-center justify-content-around align-items-center`}>
            <div className="d-flex justify-content-around">
                <NtilesChart
                    width={300}
                    height={450}
                    title={translate('sections.reports.pitcher.overall')}
                    seriesData={buildSeriesData(overallChart, player, percentiles)}
                    statTooltip={StatTooltip}
                />
                <NtilesChart
                    width={300}
                    height={402}
                    title={translate('sections.reports.pitcher.controlling-the-zone')}
                    seriesData={buildSeriesData(controllingTheZone, player, percentiles)}
                    statTooltip={StatTooltip}
                />
            </div>
            <div className="d-flex justify-content-around mt-5">
                <NtilesChart
                    width={300}
                    height={355}
                    title={translate('sections.reports.pitcher.missing-bats')}
                    seriesData={buildSeriesData(missingBats, player, percentiles)}
                    statTooltip={StatTooltip}
                />
                <NtilesChart
                    width={300}
                    height={402}
                    title={translate('sections.reports.pitcher.limiting-damage')}
                    seriesData={buildSeriesData(limitingDamage, player, percentiles)}
                    statTooltip={StatTooltip}
                />
            </div>
        </div>
    );
};

export default Charts;
